import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import useIsMobile from './useIsMobile';
import {
  ST_COLORS,
  MONTHS_AR,
  PAYMENT_METHODS,
  fmt,
  stLabel,
  projLabel,
  sortProjects,
  sortUnits,
  isAvailable,
  quarterFromMonth,
} from './constants';

const LS_LAST_CLOSER = 'ud_last_closer';

// ── DealRegisterModal ──
// Optimized for speed: when launched from the header (no unit prop), starts
// with a searchable unit picker. When launched from a row, jumps straight to
// the form with project+unit locked. Closer is a chip row (1 tap), the last
// closer is remembered. Channel auto-detects from the unit's known source
// (broker hint). Mobile: full-screen modal + sticky submit.
function DealRegisterModal({ unit: initialUnit, units, brandProjects, brokers, team, onClose, onSubmit, onAddBroker, forcedCloser, t, lang }) {
  const isMobile = useIsMobile();
  const [unit, setUnit] = useState(initialUnit || null);
  const [pickerProject, setPickerProject] = useState('');

  // Closable inventory only (متاح / محجوز / خارطة).
  const closableUnits = useMemo(
    () => (units || []).filter(isAvailable),
    [units]
  );

  // Project dropdown mirrors the Leads dropdown: the configured brand projects
  // (same list + order), plus any project that has closable units but isn't in
  // the config list so existing inventory is never hidden.
  const pickerProjects = useMemo(() => {
    const base = Array.isArray(brandProjects) ? brandProjects : [];
    const fromUnits = [...new Set(closableUnits.map((u) => u.project))];
    if (!base.length) return sortProjects(fromUnits);
    const extras = sortProjects(fromUnits.filter((p) => p && !base.includes(p)));
    return [...base, ...extras];
  }, [brandProjects, closableUnits]);

  // Units of the chosen project only.
  const pickerResults = useMemo(() => {
    if (!pickerProject) return [];
    return sortUnits(closableUnits.filter((u) => u.project === pickerProject));
  }, [closableUnits, pickerProject]);

  // Show picker when no unit selected and no specific unit was passed in.
  if (!unit) {
    return (
      <UnitPicker
        projects={pickerProjects}
        project={pickerProject}
        setProject={setPickerProject}
        results={pickerResults}
        onPick={(u) => setUnit(u)}
        onClose={onClose}
        isMobile={isMobile}
        t={t}
        lang={lang}
      />
    );
  }

  return (
    <DealForm
      unit={unit}
      onChangeUnit={() => setUnit(null)}
      canChangeUnit={!initialUnit}
      brokers={brokers}
      team={team}
      onClose={onClose}
      onSubmit={onSubmit}
      onAddBroker={onAddBroker}
      forcedCloser={forcedCloser}
      isMobile={isMobile}
      t={t}
      lang={lang}
    />
  );
}

// ── Unit picker step ──
// Two-step flow: choose project from dropdown, then pick a unit from that project's list.
function UnitPicker({ projects, project, setProject, results, onPick, onClose, isMobile, t, lang }) {
  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 540,
          maxHeight: isMobile ? '100vh' : '85vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{ fontSize: '1rem', fontWeight: 800, margin: 0 }}>
            {project
              ? (lang === 'ar' ? 'اختر الوحدة' : 'Pick a unit')
              : (lang === 'ar' ? 'اختر المشروع' : 'Pick a project')}
          </h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '12px 14px 8px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'var(--text-secondary)',
              marginBottom: 6,
            }}
          >
            {lang === 'ar' ? 'المشروع' : 'Project'}
          </label>
          <select
            autoFocus
            value={project}
            onChange={(e) => setProject(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontSize: '0.9rem',
              fontWeight: 600,
              background: 'var(--bg-card)',
              boxSizing: 'border-box',
            }}
          >
            <option value="">{lang === 'ar' ? '— اختر المشروع —' : '— Select project —'}</option>
            {projects.map((p) => (
              <option key={p} value={p}>{projLabel(p, t)}</option>
            ))}
          </select>
          {project && (
            <div
              style={{
                fontSize: '0.7rem',
                color: 'var(--text-secondary)',
                fontWeight: 600,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>
                {results.length} {lang === 'ar' ? 'وحدة قابلة للبيع' : 'closable units'}
              </span>
              <button
                onClick={() => setProject('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  padding: 0,
                }}
              >
                {lang === 'ar' ? 'تغيير المشروع' : 'Change project'}
              </button>
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1, borderTop: project ? '1px solid var(--border)' : 'none' }}>
          {!project ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {lang === 'ar' ? 'اختر المشروع لعرض الوحدات' : 'Select a project to see units'}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {lang === 'ar' ? 'لا توجد وحدات قابلة للبيع في هذا المشروع' : 'No closable units in this project'}
            </div>
          ) : (
            results.map((u, i) => {
              const color = ST_COLORS[u.status] || '#888';
              return (
                <button
                  key={u.id}
                  onClick={() => onPick(u)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                    textAlign: lang === 'ar' ? 'right' : 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-primary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', marginBottom: 2 }}>
                      {projLabel(u.project, t)} {u.unit_type ? '· ' + u.unit_type : ''} {u.neighborhood ? '· ' + u.neighborhood : ''}
                    </div>
                    <div style={{ fontSize: '0.92rem', fontWeight: 800 }}>
                      {u.unit_code}
                      {u.status === 'محجوز' && u.client_name && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 600, marginInlineStart: 8 }}>
                          · {u.client_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 10,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        background: color + '18',
                        color,
                      }}
                    >
                      {stLabel(u.status, lang)}
                    </span>
                    <span style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--accent)', direction: 'ltr' }}>
                      {fmt(u.current_price || u.base_price)} SAR
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ── Deal form step ──
function DealForm({ unit, onChangeUnit, canChangeUnit, brokers, team, onClose, onSubmit, onAddBroker, forcedCloser, isMobile, t, lang }) {
  const now = new Date();
  const defaultMonth = MONTHS_AR[now.getMonth()];
  const defaultQuarter = 'Q' + (Math.floor(now.getMonth() / 3) + 1);
  const lastCloser = typeof window !== 'undefined' ? window.localStorage?.getItem(LS_LAST_CLOSER) || '' : '';

  // Only a RESERVED unit (محجوز) has a live reservation to carry into the deal.
  // Available / off-plan units may still hold a stale client_name/broker/earnest
  // from a past reservation that ended — never pre-fill those, or the deal shows
  // a phantom customer that belongs to someone else.
  const isReserved = unit.status === 'محجوز';

  // Smart defaults (reserved units only):
  //   - If reserved via a broker (client_source matches a known broker), pre-select that broker.
  //   - Carry over existing earnest + payment method from the reservation.
  const detectedBroker = useMemo(() => {
    if (!isReserved) return null;
    const src = (unit.client_source || '').trim().toLowerCase();
    if (!src) return null;
    return brokers.find((b) => (b.name || '').toLowerCase().includes(src) || src.includes((b.name || '').toLowerCase()));
  }, [isReserved, unit.client_source, brokers]);

  const listedPrice = Number(unit.current_price || unit.base_price || 0);

  const [form, setForm] = useState({
    channel: detectedBroker ? 'broker' : 'direct',
    broker_name: detectedBroker?.name || '',
    broker_pct: detectedBroker?.default_pct || 2,
    closer: forcedCloser || (team.includes(lastCloser) ? lastCloser : (unit.closer && team.includes(unit.closer) ? unit.closer : '')),
    deal_month: defaultMonth,
    quarter: defaultQuarter,
    actual_price: listedPrice,
    earnest_money: isReserved ? (Number(unit.earnest_money) || 0) : 0,
    payment_method: isReserved && PAYMENT_METHODS.some((p) => p.value === unit.payment_method) ? unit.payment_method : 'بنك',
    // Customer fields: either picked from an existing lead OR typed fresh.
    customer_mode: 'existing',
    client_name: isReserved ? (unit.client_name || '') : '',
    client_phone: isReserved ? (unit.client_phone || '') : '',
    lead_id: null,
    note: '',
  });

  // Load deal-ready leads (interested / site_visit / offer / negotiating / won)
  // so the user can pre-fill customer details from an existing pipeline lead.
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLeadsLoading(true);
    (async () => {
      try {
        // Fetch a single batch of deal-ready leads. Server caps limit at 200.
        const statuses = ['interested', 'site_visit_scheduled', 'offer_sent', 'negotiating', 'won'];
        const results = await Promise.all(
          statuses.map((s) => axios.get('/api/leads', { params: { status: s, limit: 200 } }).then((r) => r.data?.leads || []).catch(() => []))
        );
        if (cancelled) return;
        const flat = [].concat(...results);
        // Dedupe by id; prefer most-recently-updated.
        const byId = new Map();
        flat.forEach((l) => { byId.set(l.id, l); });
        setLeads(Array.from(byId.values()).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
      } catch (e) {
        if (!cancelled) setLeads([]);
      } finally {
        if (!cancelled) setLeadsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The preloaded list is only deal-ready leads, so a customer still in an early stage
  // (e.g. a fresh website lead) wouldn't appear. When the user types, ALSO search the
  // whole CRM by name/phone so any existing customer can be found and linked.
  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    const q = leadSearch.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get('/api/leads', { params: { search: q, limit: 50 } });
        if (!cancelled) setSearchResults(r.data?.leads || []);
      } catch (e) { if (!cancelled) setSearchResults([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [leadSearch]);

  const filteredLeads = useMemo(() => {
    const q = leadSearch.trim().toLowerCase();
    if (!q) return leads.slice(0, 50);
    // Merge deal-ready matches with the any-status server search; dedupe by id.
    const local = leads.filter((l) => (l.name || '').toLowerCase().includes(q) || (l.phone || '').toLowerCase().includes(q));
    const byId = new Map();
    [...local, ...searchResults].forEach((l) => { if (l && l.id && !byId.has(l.id)) byId.set(l.id, l); });
    return Array.from(byId.values()).slice(0, 50);
  }, [leads, leadSearch, searchResults]);

  const pickLead = (l) => {
    setForm((p) => ({ ...p, customer_mode: 'existing', client_name: l.name || '', client_phone: l.phone || '', lead_id: l.id }));
    setLeadSearch('');
  };
  const clearLead = () => setForm((p) => ({ ...p, lead_id: null, client_name: '', client_phone: '' }));

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const price = Number(form.actual_price) || 0;
  const priceChanged = price !== listedPrice;
  const needsBroker = form.channel === 'broker' || form.channel === 'mixed';

  // "Other" inline broker creation — appears when user picks "أخرى" in the broker dropdown.
  const [addingBroker, setAddingBroker] = useState(false);
  const [newBroker, setNewBroker] = useState({ name: '', default_pct: 2 });
  const [addingErr, setAddingErr] = useState('');
  const [savingBroker, setSavingBroker] = useState(false);

  // When the brokers list updates after an add, auto-select the new one and exit add mode.
  useEffect(() => {
    if (!addingBroker || !newBroker.name) return;
    const found = brokers.find((b) => b.name === newBroker.name);
    if (found) {
      setForm((p) => ({ ...p, broker_name: found.name, broker_pct: found.default_pct || p.broker_pct }));
      setAddingBroker(false);
      setNewBroker({ name: '', default_pct: 2 });
      setSavingBroker(false);
    }
  }, [brokers, addingBroker, newBroker.name]);

  const handleAddNewBroker = async () => {
    const name = (newBroker.name || '').trim();
    if (!name) { setAddingErr(lang === 'ar' ? 'اسم الوسيط مطلوب' : 'Broker name required'); return; }
    if (brokers.some((b) => b.name === name)) { setAddingErr(lang === 'ar' ? 'الوسيط موجود بالفعل' : 'Broker already exists'); return; }
    const pct = Number(newBroker.default_pct);
    if (!(pct > 0 && pct <= 10)) { setAddingErr(lang === 'ar' ? 'نسبة غير صحيحة' : 'Invalid percentage'); return; }
    setAddingErr('');
    setSavingBroker(true);
    try {
      await onAddBroker({ name, default_pct: pct });
      // Effect above will auto-select on the next brokers prop update.
    } catch (e) {
      setSavingBroker(false);
      setAddingErr(lang === 'ar' ? 'فشل الحفظ' : 'Save failed');
    }
  };

  const brokerCommission = useMemo(() => {
    if (!needsBroker) return 0;
    return Math.round((price * Number(form.broker_pct || 0)) / 100);
  }, [price, form.broker_pct, needsBroker]);

  const brokerOnly = form.channel === 'broker';
  const validationError = useMemo(() => {
    if (!form.client_name || !form.client_name.trim()) return lang === 'ar' ? 'اسم العميل مطلوب' : 'Customer name required';
    if (!brokerOnly && !form.closer) return lang === 'ar' ? 'اختر منفّذ الصفقة' : 'Select a closer';
    if (needsBroker && !form.broker_name) return lang === 'ar' ? 'اختر الوسيط' : 'Select a broker';
    if (price <= 0) return lang === 'ar' ? 'سعر الوحدة غير محدد' : 'Unit price not set';
    return null;
  }, [form, needsBroker, brokerOnly, price, lang]);

  const submit = () => {
    if (validationError) return;
    if (form.closer) {
      try { window.localStorage?.setItem(LS_LAST_CLOSER, form.closer); } catch (e) {}
    }
    onSubmit({
      project: unit.project,
      unit_code: unit.unit_code,
      price: listedPrice,
      actual_price: price,
      channel: form.channel,
      broker_name: needsBroker ? form.broker_name : '',
      broker_pct: needsBroker ? Number(form.broker_pct || 0) : 0,
      closer: brokerOnly ? '' : form.closer,
      deal_month: form.deal_month,
      quarter: form.quarter,
      earnest_money: Number(form.earnest_money || 0),
      payment_method: form.payment_method,
      client_name: (form.client_name || '').trim(),
      client_phone: (form.client_phone || '').trim(),
      lead_id: form.lead_id || null,
      note: (form.note || '').trim(),
    });
  };

  // Enter to submit (when valid) for keyboard-fast workflows.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !validationError) submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [validationError, form, submit]);

  const color = ST_COLORS[unit.status] || 'var(--accent)';

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 560,
          maxHeight: isMobile ? '100vh' : '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', letterSpacing: 0.4, marginBottom: 2, textTransform: 'uppercase' }}>
              {t('udRegisterDeal')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                {projLabel(unit.project, t)} — {unit.unit_code}
              </h2>
              {canChangeUnit && (
                <button
                  onClick={onChangeUnit}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: '0.66rem',
                    color: 'var(--accent)',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {lang === 'ar' ? 'تغيير' : 'Change'}
                </button>
              )}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px' }}>
          {/* Unit summary + editable deal price */}
          <div
            style={{
              background: color + '10',
              border: '1px solid ' + color + '30',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 14,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 2 }}>
                {unit.unit_type} · {unit.neighborhood}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, direction: 'ltr', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                <span>{lang === 'ar' ? 'السعر المعروض:' : 'Listed:'}</span>
                <span style={{ fontWeight: 700 }}>{fmt(listedPrice)}</span>
                <span style={{ fontWeight: 700 }}>SAR</span>
              </div>
            </div>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: 12,
                fontSize: '0.7rem',
                fontWeight: 700,
                background: color + '20',
                color,
              }}
            >
              {stLabel(unit.status, lang)}
            </span>
          </div>

          {/* Deal price (editable) */}
          <div
            style={{
              border: '1.5px solid ' + (priceChanged ? '#f59e0b' : 'var(--accent)'),
              background: (priceChanged ? '#f59e0b' : 'var(--accent)') + '10',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 800, color: priceChanged ? '#f59e0b' : 'var(--accent)', letterSpacing: 0.3 }}>
                {lang === 'ar' ? 'سعر الصفقة (قابل للتعديل)' : 'Deal price (editable)'}
              </label>
              {priceChanged && (
                <button
                  type="button"
                  onClick={() => u('actual_price', listedPrice)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#f59e0b',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    padding: 0,
                  }}
                >
                  {lang === 'ar' ? 'إعادة إلى السعر المعروض' : 'Reset to listed'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                value={form.actual_price}
                onChange={(e) => u('actual_price', e.target.value)}
                min="0"
                step="1000"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  fontSize: '1.05rem',
                  fontWeight: 800,
                  background: '#fff',
                  direction: 'ltr',
                  textAlign: lang === 'ar' ? 'right' : 'left',
                }}
              />
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>SAR</span>
            </div>
            {priceChanged && (
              <div style={{ fontSize: '0.7rem', color: '#f59e0b', marginTop: 6, fontWeight: 600, direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>
                {price > listedPrice
                  ? `+ ${fmt(price - listedPrice)} SAR ${lang === 'ar' ? 'فوق السعر المعروض' : 'above listed'}`
                  : `− ${fmt(listedPrice - price)} SAR ${lang === 'ar' ? 'تحت السعر المعروض' : 'below listed'}`}
              </div>
            )}
          </div>

          {/* Customer — always an existing lead */}
          <SectionLabel>{lang === 'ar' ? 'العميل' : 'Customer'} <span style={{ color: '#ef4444' }}>*</span></SectionLabel>

          {form.customer_mode === 'existing' ? (
            <div style={{ marginBottom: 14 }}>
              {(form.lead_id || form.client_name) ? (
                <div
                  style={{
                    background: 'rgba(16,185,129,0.08)',
                    border: '1px solid rgba(16,185,129,0.35)',
                    borderRadius: 8,
                    padding: '8px 10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.86rem', fontWeight: 800 }}>{form.client_name}
                      {!form.lead_id && <span style={{ fontSize: '0.62rem', fontWeight: 600, color: 'var(--text-secondary)', marginInlineStart: 6 }}>{lang === 'ar' ? '· من الحجز' : '· from reservation'}</span>}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', direction: 'ltr' }}>
                      {form.client_phone || '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearLead}
                    style={{
                      background: 'none',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    {lang === 'ar' ? 'تغيير' : 'Change'}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder={lang === 'ar' ? 'ابحث بالاسم أو الجوال…' : 'Search name or phone…'}
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 11px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      fontSize: '0.86rem',
                      background: 'var(--bg-card)',
                      boxSizing: 'border-box',
                      marginBottom: 6,
                    }}
                  />
                  <div
                    style={{
                      maxHeight: 180,
                      overflowY: 'auto',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: 'var(--bg-card)',
                    }}
                  >
                    {leadsLoading ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        {lang === 'ar' ? 'جارٍ التحميل…' : 'Loading…'}
                      </div>
                    ) : filteredLeads.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        {lang === 'ar' ? 'لا توجد عملاء مطابقين' : 'No matching leads'}
                      </div>
                    ) : (
                      filteredLeads.map((l, i) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => pickLead(l)}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            background: 'transparent',
                            border: 'none',
                            borderBottom: i < filteredLeads.length - 1 ? '1px solid var(--border)' : 'none',
                            cursor: 'pointer',
                            textAlign: lang === 'ar' ? 'right' : 'left',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-primary)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>{l.name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', direction: 'ltr' }}>
                              {l.phone || '—'} {l.assignedTo ? '· ' + l.assignedTo : ''}
                            </div>
                          </div>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 10,
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              background: l.status === 'won' ? 'rgba(16,185,129,0.18)' : 'rgba(99,102,241,0.14)',
                              color: l.status === 'won' ? '#10b981' : 'var(--accent)',
                            }}
                          >
                            {l.status}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 14 }}>
              <div className="input-group">
                <label>{lang === 'ar' ? 'اسم العميل' : 'Customer name'}</label>
                <input
                  type="text"
                  value={form.client_name}
                  onChange={(e) => u('client_name', e.target.value)}
                  placeholder={lang === 'ar' ? 'الاسم الكامل' : 'Full name'}
                />
              </div>
              <div className="input-group">
                <label>{lang === 'ar' ? 'الجوال' : 'Phone'}</label>
                <input
                  type="tel"
                  value={form.client_phone}
                  onChange={(e) => u('client_phone', e.target.value)}
                  placeholder="05XXXXXXXX"
                  style={{ direction: 'ltr' }}
                />
              </div>
            </div>
          )}

          {/* Channel */}
          <SectionLabel>{t('udSalesChannel')}</SectionLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[
              { k: 'direct', l: t('udDirect') },
              { k: 'broker', l: t('udBroker') },
              { k: 'mixed', l: t('udMixed') },
            ].map((ch) => (
              <button
                key={ch.k}
                onClick={() => u('channel', ch.k)}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  borderRadius: 8,
                  border: form.channel === ch.k ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                  background: form.channel === ch.k ? 'rgba(99,102,241,0.08)' : 'transparent',
                  color: form.channel === ch.k ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                {ch.l}
              </button>
            ))}
          </div>

          {/* Broker (conditional) */}
          {needsBroker && (
            <div
              style={{
                background: 'rgba(245,158,11,0.06)',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 10,
                padding: 12,
                marginBottom: 14,
              }}
            >
              <SectionLabel color="#f59e0b">{lang === 'ar' ? 'تفاصيل الوسيط' : 'Broker Details'}</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                <div className="input-group">
                  <label>{t('udBrokerName')}</label>
                  <select
                    value={addingBroker ? '__other__' : form.broker_name}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__other__') {
                        setAddingBroker(true);
                        setAddingErr('');
                        setForm((p) => ({ ...p, broker_name: '' }));
                        return;
                      }
                      setAddingBroker(false);
                      const b = brokers.find((x) => x.name === v);
                      setForm((p) => ({ ...p, broker_name: v, broker_pct: b?.default_pct || p.broker_pct }));
                    }}
                  >
                    <option value="">— {lang === 'ar' ? 'اختر' : 'Select'} —</option>
                    {brokers.map((b) => (
                      <option key={b.id} value={b.name}>
                        {b.name} ({b.default_pct}%)
                      </option>
                    ))}
                    <option value="__other__">+ {lang === 'ar' ? 'أخرى — إضافة وسيط جديد' : 'Other — add new broker'}</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>{t('udBrokerPct')}</label>
                  <input
                    type="number"
                    value={form.broker_pct}
                    onChange={(e) => u('broker_pct', e.target.value)}
                    min="1.7"
                    max="2.5"
                    step="0.1"
                    disabled={addingBroker}
                  />
                </div>
              </div>

              {/* Inline add-broker form */}
              {addingBroker && (
                <div
                  style={{
                    background: '#fff',
                    border: '1px dashed #f59e0b',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b', marginBottom: 6 }}>
                    {lang === 'ar' ? 'إضافة وسيط جديد' : 'Add new broker'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 6 }}>
                    <input
                      type="text"
                      autoFocus
                      placeholder={lang === 'ar' ? 'اسم الوسيط' : 'Broker name'}
                      value={newBroker.name}
                      onChange={(e) => setNewBroker((p) => ({ ...p, name: e.target.value }))}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        fontSize: '0.85rem',
                        background: 'var(--bg-card)',
                      }}
                    />
                    <input
                      type="number"
                      placeholder="%"
                      value={newBroker.default_pct}
                      onChange={(e) => setNewBroker((p) => ({ ...p, default_pct: e.target.value }))}
                      min="0.5"
                      max="10"
                      step="0.1"
                      style={{
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        fontSize: '0.85rem',
                        background: 'var(--bg-card)',
                      }}
                    />
                  </div>
                  {addingErr && (
                    <div style={{ color: '#ef4444', fontSize: '0.72rem', fontWeight: 600, marginBottom: 6 }}>{addingErr}</div>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleAddNewBroker}
                      disabled={savingBroker}
                      style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700, background: '#f59e0b', borderColor: '#f59e0b' }}
                    >
                      {savingBroker
                        ? (lang === 'ar' ? '\u062C\u0627\u0631\u064D\u2026' : 'Saving\u2026')
                        : (lang === 'ar' ? '\u0625\u0636\u0627\u0641\u0629' : 'Add')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setAddingBroker(false); setNewBroker({ name: '', default_pct: 2 }); setAddingErr(''); }}
                      disabled={savingBroker}
                      style={{ padding: '6px 12px', fontSize: '0.78rem' }}
                    >
                      {lang === 'ar' ? '\u0625\u0644\u063A\u0627\u0621' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ background: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: '0.74rem', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {lang === 'ar' ? 'عمولة الوسيط (تقديرية)' : 'Broker Commission (est.)'}
                </span>
                <span style={{ fontWeight: 800, color: '#f59e0b', direction: 'ltr' }}>{fmt(brokerCommission)} SAR</span>
              </div>
            </div>
          )}

          {/* Closer as chips (tap-friendly) — hidden when pure-broker channel
              (closer N/A) and when the closer is forced to the submitter (a
              sales rep is always the closer of their own deal). */}
          {!brokerOnly && !forcedCloser && (
            <>
              <SectionLabel>
                {t('udCloser')} <span style={{ color: '#ef4444' }}>*</span>
              </SectionLabel>
              {team.length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: '#ef4444', marginBottom: 14 }}>
                  {lang === 'ar' ? 'لا يوجد فريق مبيعات. أضف من الإعدادات.' : 'No sales team. Add from Settings.'}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {team.map((m) => (
                    <button
                      key={m}
                      onClick={() => u('closer', m)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 18,
                        border: form.closer === m ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                        background: form.closer === m ? 'var(--accent)' : 'var(--bg-card)',
                        color: form.closer === m ? '#fff' : 'var(--text-primary)',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minHeight: 38,
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Period + earnest — quarter auto-derives from month (Q1=يناير..مارس, etc.) */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 10 }}>
            <div className="input-group">
              <label>{t('udMonth')}</label>
              <select
                value={form.deal_month}
                onChange={(e) => {
                  const m = e.target.value;
                  setForm((p) => ({ ...p, deal_month: m, quarter: quarterFromMonth(m) }));
                }}
              >
                {MONTHS_AR.map((m) => (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>
            <div className="input-group">
              <label>{t('udQuarter')}</label>
              <input
                type="text"
                value={form.quarter}
                readOnly
                tabIndex={-1}
                style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', fontWeight: 700, cursor: 'not-allowed' }}
                title={lang === 'ar' ? 'يحدد تلقائياً حسب الشهر' : 'Auto-derived from month'}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div className="input-group">
              <label>{t('udEarnest')} (SAR)</label>
              <input type="number" value={form.earnest_money} onChange={(e) => u('earnest_money', e.target.value)} min="0" />
            </div>
            <div className="input-group">
              <label>{t('udPayment')}</label>
              <select value={form.payment_method} onChange={(e) => u('payment_method', e.target.value)}>
                {PAYMENT_METHODS.map((p) => (<option key={p.value} value={p.value}>{p[lang]}</option>))}
              </select>
            </div>
          </div>

          {/* Optional note — free-form context about the client / negotiation. */}
          <div className="input-group" style={{ marginTop: 8, marginBottom: 4 }}>
            <label>
              {lang === 'ar' ? 'ملاحظة (اختياري)' : 'Note (optional)'}
            </label>
            <textarea
              value={form.note}
              onChange={(e) => u('note', e.target.value)}
              placeholder={lang === 'ar' ? 'تفاصيل عن العميل أو الصفقة…' : 'Context about the client or deal…'}
              rows={3}
              maxLength={2000}
              style={{
                width: '100%',
                padding: '9px 11px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                fontSize: '0.86rem',
                background: 'var(--bg-card)',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {!isMobile && (
            <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginTop: 6, textAlign: lang === 'ar' ? 'right' : 'left' }}>
              {lang === 'ar' ? 'اختصار: ⌘/Ctrl + Enter للتسجيل' : 'Tip: ⌘/Ctrl + Enter to submit'}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {validationError && (
            <div
              style={{
                background: '#ef44441a',
                border: '1px solid #ef444440',
                borderRadius: 8,
                padding: '6px 10px',
                fontSize: '0.74rem',
                color: '#ef4444',
                fontWeight: 700,
              }}
            >
              {validationError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1, padding: '10px' }}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!!validationError}
              style={{ flex: 2, fontWeight: 800, padding: '10px', opacity: validationError ? 0.6 : 1 }}
            >
              {t('udRegisterDeal')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, color }) {
  return (
    <div
      style={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: color || 'var(--text-secondary)',
        marginBottom: 7,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {children}
    </div>
  );
}

// Avoid unused-import warning while keeping the helper available.
// eslint-disable-next-line no-unused-vars
const _keep = sortProjects;

export default DealRegisterModal;
